import { useEffect, useMemo, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { hu } from 'date-fns/locale';
import { PiPlayCircleDuotone } from 'react-icons/pi';

import OptimizationPipelinePopup, {
    OptimizationPipelinePopupHeader,
    PopupBody,
    PopupToolbar,
    PopupToolbarGroup,
    PopupToolbarChip,
    PopupExportButtons
} from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';

import OptimizationPipelinePopupTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopupTable';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';
import { getSoftStatusClass } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/colorUtils';
import { parseTsToDate } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/dateTime';
import { buildExportFilename, exportPopupTable } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/exportUtils';
import { STATUS_BADGE_CLASS, STATUS_CELL_CLASS, DYNAMIC_VALIDATION_STATE_META, DYNAMIC_VALIDATION_DETAIL_GROUP_META } from 'app/admin/optimizationPipelineV2/component/pipeline/utils/constantUtils';

const VIEW_SUMMARY = 'summary';
const VIEW_COMPARISON = 'comparison';
const VIEW_BASE = 'base_values';
const VIEW_BEST = 'best_values';
const VIEW_MONITOR = 'variables_to_monitor';
const VIEW_ALARM = 'alarm_variables';

function isPlainObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value);
}

function getStatusClass(color, baseClassName) {
    return baseClassName + ' ' + getSoftStatusClass(color);
}

function safeDate(value) {
    return parseTsToDate(value);
}

function formatDisplayTimestamp(value) {
    const date = safeDate(value);

    return date ? format(date, 'yyyy.MM.dd. HH:mm:ss', { locale: hu }) : '-';
}

function formatDisplayDate(value) {
    const date = safeDate(value);

    return date ? format(date, 'yyyy.MM.dd.', { locale: hu }) : '-';
}

function formatNumber(value) {
    if (value === null || value === undefined || value === '') return '-';

    const number = Number(value);

    if (!Number.isFinite(number)) return String(value);

    return number.toLocaleString('hu-HU', {
        maximumFractionDigits: 4
    });
}

function formatExportDetailValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);

    return String(value);
}

function getPrettyError(error) {
    return String(error?.message || error || 'Unknown error');
}

function getRows(data) {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (data) return [data];

    return [];
}

function getStateValue(row) {
    return row?.state || (row?.finished === false ? 'NOT_FINISHED' : null);
}

function getStateExportValue(row) {
    const value = getStateValue(row);
    const meta = value ? DYNAMIC_VALIDATION_STATE_META[value] : null;

    return meta?.label || value || '-';
}

function renderStateBadge(row) {
    const value = getStateValue(row);
    const meta = value ? DYNAMIC_VALIDATION_STATE_META[value] : null;

    if (meta) {
        return <div className={getStatusClass(meta.color, STATUS_BADGE_CLASS)}>{meta.label}</div>;
    }

    return <div className={getStatusClass('gray', STATUS_BADGE_CLASS)}>{value || '-'}</div>;
}

function renderBooleanBadge(value, trueLabel = 'Yes', falseLabel = 'No') {
    if (value === true) return <div className={getStatusClass('green', STATUS_BADGE_CLASS)}>{trueLabel}</div>;
    if (value === false) return <div className={getStatusClass('gray', STATUS_BADGE_CLASS)}>{falseLabel}</div>;

    return <div className={getStatusClass('red', STATUS_BADGE_CLASS)}>Missing</div>;
}

function renderAlarmBadge(value) {
    if (value === true) return <div className={getStatusClass('red', STATUS_BADGE_CLASS)}>Yes</div>;
    if (value === false) return <div className={getStatusClass('green', STATUS_BADGE_CLASS)}>No</div>;

    return <div className={getStatusClass('gray', STATUS_BADGE_CLASS)}>Missing</div>;
}

function renderDiffCell(value, suffix = '') {
    const number = Number(value);

    if (!Number.isFinite(number)) {
        return <div className={getStatusClass('gray', STATUS_CELL_CLASS)}><span className='font-semibold'>-</span></div>;
    }

    if (number > 0) {
        return <div className={getStatusClass('green', STATUS_CELL_CLASS)}><span className='font-semibold'>+{formatNumber(number)}{suffix}</span></div>;
    }

    if (number < 0) {
        return <div className={getStatusClass('yellow', STATUS_CELL_CLASS)}><span className='font-semibold'>{formatNumber(number)}{suffix}</span></div>;
    }

    return <div className={getStatusClass('gray', STATUS_CELL_CLASS)}><span className='font-semibold'>0{suffix}</span></div>;
}

function buildComparisonRows(row) {
    if (!row) return [];

    const keys = new Set();

    Object.keys(row.base_values || {}).forEach(key => keys.add(key));
    Object.keys(row.best_values || {}).forEach(key => keys.add(key));

    return Array.from(keys).sort().map((key) => {
        const base = row.base_values?.[key];
        const best = row.best_values?.[key];

        const baseNumber = Number(base);
        const bestNumber = Number(best);
        const hasNumberDiff = Number.isFinite(baseNumber) && Number.isFinite(bestNumber);
        const diff = hasNumberDiff ? bestNumber - baseNumber : null;
        const diffPercent = hasNumberDiff && baseNumber !== 0 ? (diff / baseNumber) * 100 : null;

        return {
            key,
            base,
            best,
            diff,
            diffPercent
        };
    });
}

function buildDetailsRows(row, groupKey) {
    const source = row?.[groupKey];

    if (!isPlainObject(source)) return [];

    return Object.entries(source).map(([key, value]) => ({
        key,
        value
    }));
}

function buildSummaryRows(row, selectedMode) {
    if (!row) return [];

    return [
        { key: 'Selected date', value: selectedMode?.dateLabel || '-' },
        { key: 'Selected source', value: selectedMode?.label || '-' },
        { key: 'Run timestamp', value: formatDisplayTimestamp(row.ts) },
        { key: 'State', value: getStateExportValue(row), renderValue: () => renderStateBadge(row) },
        { key: 'Finished', value: row.finished === true ? 'Yes' : row.finished === false ? 'No' : 'Missing', renderValue: () => renderBooleanBadge(row.finished) },
        { key: 'Converged', value: row.converged === true ? 'Yes' : row.converged === false ? 'No' : 'Missing', renderValue: () => renderBooleanBadge(row.converged) },
        { key: 'Alarm', value: row.alarm === true ? 'Yes' : row.alarm === false ? 'No' : 'Missing', renderValue: () => renderAlarmBadge(row.alarm) },
        { key: 'Base values', value: Object.keys(row.base_values || {}).length },
        { key: 'Best values', value: Object.keys(row.best_values || {}).length },
        { key: 'Monitor variables', value: Object.keys(row.variables_to_monitor || {}).length },
        { key: 'Alarm variables', value: Object.keys(row.alarm_variables || {}).length }
    ];
}

function resolveDailyRun(rows, ts) {
    const sortedRows = (Array.isArray(rows) ? rows : [])
        .filter(row => row?.ts)
        .sort((a, b) => {
            const dateA = safeDate(a.ts);
            const dateB = safeDate(b.ts);

            return (dateB?.getTime?.() || 0) - (dateA?.getTime?.() || 0);
        });

    if (!sortedRows.length) {
        return {
            row: null,
            mode: null
        };
    }

    const selectedDate = safeDate(ts);

    if (selectedDate) {
        const sameDayRow = sortedRows.find((row) => {
            const rowDate = safeDate(row?.ts);

            return rowDate && isSameDay(rowDate, selectedDate);
        });

        if (sameDayRow) {
            return {
                row: sameDayRow,
                mode: {
                    type: 'exact-day',
                    label: 'Exact day match',
                    dateLabel: formatDisplayDate(selectedDate)
                }
            };
        }

        const previousRows = sortedRows.filter((row) => {
            const rowDate = safeDate(row?.ts);

            return rowDate && rowDate.getTime() <= selectedDate.getTime();
        });

        if (previousRows.length) {
            return {
                row: previousRows[0],
                mode: {
                    type: 'latest-before-selected-date',
                    label: 'Latest available before selected date',
                    dateLabel: formatDisplayDate(selectedDate)
                }
            };
        }
    }

    return {
        row: sortedRows[0],
        mode: {
            type: 'latest-available',
            label: 'Latest available run',
            dateLabel: selectedDate ? formatDisplayDate(selectedDate) : '-'
        }
    };
}

export default function DynamicValidationDetailsPopup(props) {
    const {
        popup,
        closePopup,
        ts,
        getOnDemandResource,
        ensureOnDemandResource
    } = useOptimizationPipeline();

    const [view, setView] = useState(VIEW_SUMMARY);
    const [summarySort, setSummarySort] = useState({ key: 'key', direction: 'asc' });
    const [comparisonSort, setComparisonSort] = useState({ key: 'key', direction: 'asc' });
    const [detailsSort, setDetailsSort] = useState({ key: 'key', direction: 'asc' });

    const open = !!popup && popup.type === 'DYNAMIC_VALIDATION_DETAILS';

    const edtDynamicCheckLogResource = getOnDemandResource('edtDynamicCheckLogData');
    const edtDynamicCheckLogData = edtDynamicCheckLogResource?.data;

    useEffect(() => {
        if (!open) return;

        setView(VIEW_SUMMARY);
        setSummarySort({ key: 'key', direction: 'asc' });
        setComparisonSort({ key: 'key', direction: 'asc' });
        setDetailsSort({ key: 'key', direction: 'asc' });
        ensureOnDemandResource('edtDynamicCheckLogData');
    }, [open, ts, ensureOnDemandResource]);

    const rows = useMemo(() => {
        return getRows(edtDynamicCheckLogData);
    }, [edtDynamicCheckLogData]);

    const resolvedRunData = useMemo(() => {
        return resolveDailyRun(rows, ts);
    }, [rows, ts]);

    const selectedRun = resolvedRunData.row;
    const selectedMode = resolvedRunData.mode;

    const summaryRows = useMemo(() => {
        return buildSummaryRows(selectedRun, selectedMode);
    }, [selectedRun, selectedMode]);

    const comparisonRows = useMemo(() => {
        return buildComparisonRows(selectedRun);
    }, [selectedRun]);

    const detailsRows = useMemo(() => {
        return buildDetailsRows(selectedRun, view);
    }, [selectedRun, view]);

    const isLoading = !!edtDynamicCheckLogResource?.loading;
    const errorMessage = edtDynamicCheckLogResource?.error;
    const selectedViewMeta = DYNAMIC_VALIDATION_DETAIL_GROUP_META[view] || null;

    const summaryTableConfig = useMemo(() => ({
        tableMinWidth: 560,
        sort: { key: 'key', direction: 'asc' },
        states: {
            loadingTitle: 'Loading EDT Dynamic Check details...',
            emptyTitle: 'No EDT Dynamic Check details available',
            errorTitle: 'Failed to load EDT Dynamic Check details'
        },
        columns: [
            {
                key: 'key',
                label: 'Field',
                exportLabel: 'Field',
                minWidth: 240,
                sortable: true,
                renderCell: ({ row }) => <span>{row.key}</span>
            },
            {
                key: 'value',
                label: 'Value',
                exportLabel: 'Value',
                minWidth: 260,
                sortable: true,
                exportValue: row => row.value,
                renderCell: ({ row }) => typeof row.renderValue === 'function' ? row.renderValue() : row.value
            }
        ]
    }), []);

    const comparisonTableConfig = useMemo(() => ({
        tableMinWidth: 760,
        sort: { key: 'key', direction: 'asc' },
        states: {
            loadingTitle: 'Loading base / best values...',
            emptyTitle: 'No base / best values available',
            errorTitle: 'Failed to load base / best values'
        },
        columns: [
            {
                key: 'key',
                label: 'Variable',
                exportLabel: 'Variable',
                minWidth: 220,
                sortable: true,
                renderCell: ({ row }) => <span>{row.key}</span>
            },
            {
                key: 'base',
                label: 'Base',
                exportLabel: 'Base',
                minWidth: 130,
                align: 'right',
                sortable: true,
                exportValue: row => row.base,
                renderCell: ({ row }) => formatNumber(row.base)
            },
            {
                key: 'best',
                label: 'Best',
                exportLabel: 'Best',
                minWidth: 130,
                align: 'right',
                sortable: true,
                exportValue: row => row.best,
                renderCell: ({ row }) => formatNumber(row.best)
            },
            {
                key: 'diff',
                label: 'Difference',
                exportLabel: 'Difference',
                minWidth: 130,
                align: 'right',
                sortable: true,
                exportValue: row => row.diff,
                renderCell: ({ row }) => renderDiffCell(row.diff)
            },
            {
                key: 'diffPercent',
                label: 'Difference %',
                exportLabel: 'Difference %',
                minWidth: 130,
                align: 'right',
                sortable: true,
                exportValue: row => row.diffPercent,
                renderCell: ({ row }) => renderDiffCell(row.diffPercent, '%')
            }
        ]
    }), []);

    const detailsTableConfig = useMemo(() => ({
        tableMinWidth: 560,
        sort: { key: 'key', direction: 'asc' },
        states: {
            loadingTitle: 'Loading values...',
            emptyTitle: 'No values available',
            errorTitle: 'Failed to load values'
        },
        columns: [
            {
                key: 'key',
                label: 'Variable',
                exportLabel: 'Variable',
                minWidth: 300,
                sortable: true,
                renderCell: ({ row }) => <span>{row.key}</span>
            },
            {
                key: 'value',
                label: 'Value',
                exportLabel: 'Value',
                minWidth: 180,
                align: 'right',
                sortable: true,
                exportValue: row => formatExportDetailValue(row.value),
                renderCell: ({ row }) => formatNumber(row.value)
            }
        ]
    }), []);

    const activeRows = view === VIEW_SUMMARY ? summaryRows : view === VIEW_COMPARISON ? comparisonRows : detailsRows;
    const activeTableConfig = view === VIEW_SUMMARY ? summaryTableConfig : view === VIEW_COMPARISON ? comparisonTableConfig : detailsTableConfig;
    const activeSort = view === VIEW_SUMMARY ? summarySort : view === VIEW_COMPARISON ? comparisonSort : detailsSort;
    const setActiveSort = view === VIEW_SUMMARY ? setSummarySort : view === VIEW_COMPARISON ? setComparisonSort : setDetailsSort;

    const exportDisabled = isLoading || !!errorMessage || !activeRows.length;

    const handleExport = (formatType) => {
        const namePart = view === VIEW_SUMMARY ? 'summary' : view === VIEW_COMPARISON ? 'base-vs-best' : (selectedViewMeta?.filename || 'details');

        exportPopupTable({
            tableConfig: activeTableConfig,
            rows: activeRows,
            sort: activeSort,
            filename: buildExportFilename('dynamic-validation-details-' + namePart, selectedRun?.ts || ts),
            format: formatType
        });
    };

    return (
        <OptimizationPipelinePopup open={open} onClose={closePopup} width={popup?.width || '1280px'} minWidth={popup?.minWidth || 640} maxWidth={popup?.maxWidth || 1440} className={popup?.className}>

            <OptimizationPipelinePopupHeader
                title='EDT Dynamic Check Details'
                subtitle={selectedRun ? selectedMode?.label + ': ' + formatDisplayTimestamp(selectedRun.ts) : 'Daily EDT Dynamic Check details'}
                icon={PiPlayCircleDuotone}
                onClose={closePopup}
            >
                <PopupExportButtons disabled={exportDisabled} onExcel={() => handleExport('excel')} onCsv={() => handleExport('csv')} />
            </OptimizationPipelinePopupHeader>

            <PopupToolbar className='flex-col items-stretch'>
                <div className='w-full flex flex-wrap items-center justify-between gap-10'>
                    <PopupToolbarGroup label='View:'>
                        <PopupToolbarChip label='Summary' active={view === VIEW_SUMMARY} onClick={() => { setView(VIEW_SUMMARY); }} />
                        <PopupToolbarChip label='Base vs Best' active={view === VIEW_COMPARISON} disabled={!selectedRun} onClick={() => { setView(VIEW_COMPARISON); }} />
                        <PopupToolbarChip label='Base values' active={view === VIEW_BASE} disabled={!selectedRun} onClick={() => { setView(VIEW_BASE); }} />
                        <PopupToolbarChip label='Best values' active={view === VIEW_BEST} disabled={!selectedRun} onClick={() => { setView(VIEW_BEST); }} />
                        <PopupToolbarChip label='Monitor variables' active={view === VIEW_MONITOR} disabled={!selectedRun} onClick={() => { setView(VIEW_MONITOR); }} />
                        <PopupToolbarChip label='Alarm variables' active={view === VIEW_ALARM} disabled={!selectedRun} onClick={() => { setView(VIEW_ALARM); }} />
                    </PopupToolbarGroup>

                    {selectedRun && (
                        <div className='text-11 text-gray-500'>
                            Selected date: <span>{formatDisplayDate(ts)}</span>
                        </div>
                    )}
                </div>

                {selectedRun && (
                    <div className='w-full flex flex-wrap items-center gap-8 text-11 text-gray-500'>
                        <span>Selected run:</span>
                        <span>{formatDisplayTimestamp(selectedRun.ts)}</span>
                        {renderStateBadge(selectedRun)}
                        <span className='opacity-50'>/</span>
                        <span>{selectedMode?.label || '-'}</span>
                    </div>
                )}
            </PopupToolbar>

            <PopupBody padding={false} scroll>
                <OptimizationPipelinePopupTable
                    tableConfig={activeTableConfig}
                    rows={activeRows}
                    loading={isLoading}
                    error={errorMessage ? getPrettyError(errorMessage) : null}
                    rowKey={(row, index) => row?.key || index}
                    minHeight={360}
                    sort={activeSort}
                    onSortChange={setActiveSort}
                />
            </PopupBody>

        </OptimizationPipelinePopup>
    );
}